'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

export default function Breadcrumbs() {
  const pathname = usePathname();
  
  // Remove empty segments and convert to readable format
  const segments = pathname
    .split('/')
    .filter(segment => segment.length > 0)
    .map(segment => ({
      name: formatSegmentName(segment),
      path: getPathUpToSegment(segment, pathname)
    }));

  if (segments.length === 0) return null;

  return (
    <nav aria-label="breadcrumb" className="text-gray-600 mb-1 text-sm">
      <ol className="flex items-center space-x-1">
        <li>
          <Link href="/" className="hover:text-blue-600">Home</Link>
        </li>
        {segments.map((segment, index) => (
          <li key={segment.path} className="flex items-center">
            <span className="mx-1">/</span>
            {index === segments.length - 1 ? (
              <span className="text-gray-400">{segment.name}</span>
            ) : (
              <Link href={segment.path} className="hover:text-blue-600">
                {segment.name}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function formatSegmentName(segment: string): string {
  // Handle dynamic routes
  if (segment.startsWith('[') && segment.endsWith(']')) {
    return segment.slice(1, -1);
  }

  // Special handling for category and converter routes
  if (segment === 'category') {
    return 'Categories';
  }

  // Convert kebab-case to Title Case
  const formatted = segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  // Remove trailing 's' from plural words for better readability
  if (formatted.endsWith('s') && formatted.length > 1) {
    return formatted.slice(0, -1);
  }

  return formatted;
}

function getPathUpToSegment(segment: string, pathname: string): string {
  const segmentIndex = pathname.split('/').indexOf(segment);
  return pathname
    .split('/')
    .slice(0, segmentIndex + 1)
    .join('/');
}
